import React, { useState, useEffect } from 'react';
import { Check, User, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const AVATAR_STYLES = [
  // --- PEOPLE ---
  { id: 'google', name: 'Original', type: 'image' },
  { id: 'avataaars', name: 'Peeps 1', type: 'dicebear' },
  { id: 'avataaars-neutral', name: 'Peeps 2', type: 'dicebear' },
  { id: 'open-peeps', name: 'Peeps 3', type: 'dicebear' },
  { id: 'croodles', name: 'Doodle Peeps', type: 'dicebear' },
  { id: 'notionists', name: 'B&W Peeps', type: 'dicebear' },
  { id: 'lorelei', name: 'B&W Peeps 2', type: 'dicebear' },
  { id: 'pixel-art', name: '8-Bit Peeps', type: 'dicebear' },
  
  // --- CREATURES ---
  { id: 'bottts', name: 'Bots', type: 'dicebear' },
  { id: 'bottts-neutral', name: 'Bots 2', type: 'dicebear' },
  { id: 'thumbs', name: 'Thumbs', type: 'dicebear' },

  // --- ART ---
  { id: 'icons', name: 'Symbols', type: 'dicebear' }, // Random items (anchors, clouds, bells)
  { id: 'identicon', name: 'Identicons', type: 'dicebear' }, // Geometric data-patterns
  { id: 'glass', name: 'Glass', type: 'dicebear' },
  { id: 'rings', name: 'Rings', type: 'dicebear' },
  { id: 'shapes', name: 'Abstract', type: 'dicebear' }, // Modern geometric art

];

export default function AvatarPicker({ currentStyle, currentAvatarUrl, username, onSelect }) {
  // Store a random seed suffix to allow "Shuffling"
  const [seedSuffix, setSeedSuffix] = useState('');

  // Helper to get clean style name (removes the :seed part if it exists)
  const getBaseStyle = (fullStyleString) => {
    if (!fullStyleString) return 'google';
    return fullStyleString.split(':')[0];
  };

  const activeBaseStyle = getBaseStyle(currentStyle);

  const handleShuffle = (e) => {
    e.preventDefault(); // Prevent form submission if inside a form
    const newSuffix = Math.random().toString(36).substring(7);
    setSeedSuffix(newSuffix);
    
    // If a dicebear style is currently selected, immediately update the selection with new seed
    if (activeBaseStyle !== 'google') {
      onSelect(`${activeBaseStyle}:${username}-${newSuffix}`);
    }
  };

  const handleSelect = (styleId) => {
    if (styleId === 'google') {
      onSelect('google');
    } else {
      // Save as "style:seed" so the backend knows which specific one to keep
      onSelect(`${styleId}:${username}-${seedSuffix}`);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-heading uppercase text-muted-foreground">Choose Avatar</label>
        <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={handleShuffle}
            className="h-6 px-2 text-xs text-primary hover:text-primary hover:bg-primary/10"
        >
            <RefreshCw className="w-3 h-3 mr-1" />
            Shuffle Variations
        </Button>
      </div>
      
      <div className="grid grid-cols-4 gap-3">
        {AVATAR_STYLES.map((style) => {
          const isSelected = activeBaseStyle === style.id;
          let previewUrl = '';

          if (style.type === 'dicebear') {
            // Use username + suffix as the seed
            const seed = `${username || 'user'}-${seedSuffix}`;
            previewUrl = `https://api.dicebear.com/9.x/${style.id}/svg?seed=${seed}`;
          } else {
            previewUrl = currentAvatarUrl;
          }

          return (
            <button
              key={style.id}
              type="button"
              onClick={() => handleSelect(style.id)}
              className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                isSelected 
                  ? 'border-primary ring-2 ring-primary/20 scale-95' 
                  : 'border-border hover:border-primary/50 hover:scale-105'
              }`}
            >
              <div className="absolute inset-0 bg-muted/30" />
              
              {/* Image Preview */}
              {previewUrl ? (
                <img 
                  src={previewUrl} 
                  alt={style.name}
                  className="w-full h-full object-cover" 
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
                  <User className="w-6 h-6" />
                </div>
              )}

              {/* Selection Checkmark */}
              {isSelected && (
                <div className="absolute inset-0 bg-primary/20 flex items-center justify-center backdrop-blur-[1px]">
                  <div className="bg-primary text-primary-foreground rounded-full p-1 shadow-lg">
                    <Check className="w-4 h-4" />
                  </div>
                </div>
              )}
              
              <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5 uppercase font-bold">
                {style.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}